import type { KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { get } from '@/lib/api';
import { tr } from '@/lib/i18n-core';
import { Button, Select } from '@/components/ui/primitives';
import { DiagnosisPicker } from '../../opd/emr/DiagnosisPicker';
import { selectableSpecialties, specialtyOptionLabel, type CodedCause, type SpecialtyOption } from './stay-codes';

const NOTHING_CHOSEN: ReadonlySet<string> = new Set();

export function useSpecialties(enabled = true) {
  return useQuery({
    queryKey: ['specialties'],
    queryFn: () => get<SpecialtyOption[]>('/org/specialties'),
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function SpecialtySelect({
  id,
  value,
  onChange,
  specialties,
  disabled,
  'aria-describedby': describedBy,
}: {
  id?: string;
  value: string;
  onChange: (specialtyId: string) => void;
  specialties: readonly SpecialtyOption[];
  disabled?: boolean;
  'aria-describedby'?: string;
}) {
  return (
    <Select id={id} aria-describedby={describedBy} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      <option value="">{tr('ipd', 'specialty_none')}</option>
      {selectableSpecialties(specialties, value).map((specialty) => (
        <option key={specialty.id} value={specialty.id}>
          {specialtyOptionLabel(specialty)}
        </option>
      ))}
    </Select>
  );
}

function keepEnterInTheSearch(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key === 'Enter' && event.target instanceof HTMLInputElement) event.preventDefault();
}

export function CauseOfDeathField({
  value,
  onChange,
  departmentId,
  disabled,
}: {
  value: CodedCause | null;
  onChange: (cause: CodedCause | null) => void;
  departmentId: string | null;
  disabled?: boolean;
}) {
  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
        <span className="font-mono text-xs font-semibold text-primary" dir="ltr">
          {value.code}
        </span>
        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{value.title}</span>
        {!disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} aria-label={tr('ipd', 'change_the_cause_of_death')}>
            <X className="h-4 w-4" aria-hidden />
          </Button>
        )}
      </div>
    );
  }
  return (
    <div onKeyDown={keepEnterInTheSearch}>
      <DiagnosisPicker readOnly={Boolean(disabled)} chosen={NOTHING_CHOSEN} departmentId={departmentId} onAdd={(pick) => onChange({ code: pick.code, title: pick.title })} />
    </div>
  );
}
